import { useMemo, useState, useCallback, useRef } from 'react'
import type { Partner } from '@/data'
import { getPartners } from '@/api/lop/partners/getPartners'

const PER_PAGE = 100
const DEBOUNCE_MS = 300

interface PartnerOption {
  value: number | string
  label: string
}

interface UsePartnerSearchOptions {
  /** Map a Partner to a select option. Defaults to { value: id, label: name } */
  mapOption?: (p: Partner) => PartnerOption
}

const defaultMap = (p: Partner): PartnerOption => ({
  value: p.id,
  label: `${p.name} (${p.affiliate_id})`,
})

export function usePartnerSearch(opts?: UsePartnerSearchOptions) {
  const mapOption = opts?.mapOption ?? defaultMap

  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [search, setSearch] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
  const abortRef = useRef(0)

  const fetchPage = useCallback(
    async (query: string, pageNum: number, append: boolean) => {
      const id = ++abortRef.current
      setLoading(true)
      try {
        const filters = query ? [{ key: 'partner', value: query }] : undefined
        const res = await getPartners({
          filters,
          per_page: PER_PAGE,
          page: pageNum,
        })
        if (id !== abortRef.current) return
        setPartners((prev) => (append ? [...prev, ...res.data] : res.data))
        setHasMore(pageNum < res.meta.last_page)
        setPage(pageNum)
      } catch {
        if (id !== abortRef.current) return
        if (!append) setPartners([])
        setHasMore(false)
      } finally {
        if (id === abortRef.current) setLoading(false)
      }
    },
    [],
  )

  const onSearch = useCallback(
    (value: string) => {
      setSearch(value)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        setPartners([])
        setHasMore(true)
        fetchPage(value, 1, false)
      }, DEBOUNCE_MS)
    },
    [fetchPage],
  )

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return
    fetchPage(search, page + 1, true)
  }, [loading, hasMore, search, page, fetchPage])

  // Load initial page on first call
  const initRef = useRef(false)
  if (!initRef.current) {
    initRef.current = true
    fetchPage('', 1, false)
  }

  const options = useMemo(() => partners.map(mapOption), [partners, mapOption])

  return { options, loading, hasMore, onSearch, loadMore }
}
